import React, { useEffect, useState } from "react";
import { Button, Checkbox, Radio, Loader, Icon, Header, Flag } from "semantic-ui-react";
import { EyeIcon } from "lucide-react";

import "./index.scss";

import VitalityGauge from "./VitalityGauge";

import api from "../../services/api";

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    gender: "",
    age: "",
    needs: [],
    screenTime: "",
    currentPractices: [],
    visualIssues: [],
    postureIssues: [],
  });
  const [contact, setContact] = useState(false);

  const nextStep = async () => {
    await saveAnswers();
    setCurrentStep(currentStep + 1);
  };

  const saveAnswers = async () => {
    try {
      const { data } = await api.post(`/api/questionnaire`, answers);
      setAnswers(data);
    } catch (error) {
      console.error(error);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const slides = [];
  slides.push(<Welcome />);
  slides.push(<Gender answers={answers} setAnswers={setAnswers} />);
  slides.push(<AgeRange answers={answers} setAnswers={setAnswers} />);
  slides.push(<Needs answers={answers} setAnswers={setAnswers} />);
  slides.push(<Transition />);
  slides.push(<ScreenTime answers={answers} setAnswers={setAnswers} />);
  slides.push(<CurrentPractices answers={answers} setAnswers={setAnswers} />);
  slides.push(<VisualIssues answers={answers} setAnswers={setAnswers} />);
  slides.push(<PostureIssues answers={answers} setAnswers={setAnswers} />);
  slides.push(<ThankYou />);
  slides.push(<RiskIndex answers={answers} setAnswers={setAnswers} />);
  slides.push(<Program onClick={() => setContact(true)} />);

  return (
    <div className="questionnaire-container">
      <div
        style={{
          width: "100%",
          background: "linear-gradient(180deg, #3588C5 0%, #2FA0BF 100%)",
          padding: "15px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <div className="logo" style={{ color: "white", fontWeight: "bold", fontSize: "18px" }}>
          VIZYGOOD
        </div>
        <div style={{ color: "#1EABD7", display: "flex", alignItems: "center", backgroundColor: "white", borderRadius: "6px", gap: "4px", padding: "5px 10px", fontWeight: 500 }}>
          <EyeIcon size={18} color="#1EABD7" />
          <span>Test - Votre indice de risque</span>
        </div>
        <div className="user-menu" style={{ display: "flex", alignItems: "center" }}>
          <Flag name="fr" size={18} color="#1EABD7" />
        </div>
      </div>
      <div style={{ display: "flex", width: "100%", gap: "4px", padding: "10px 20px 30px" }}>
        {[...Array(slides.length)].map((_, index) => (
          <div
            key={index}
            style={{
              flex: 1,
              height: "8px",
              backgroundColor: index <= currentStep ? "#1EABD7" : "#e0e1e2",
              borderRadius: "4px",
              transition: "background-color 0.3s ease",
            }}
          />
        ))}
      </div>
      {contact && <Contact answers={answers} />}
      {!contact && (
        <div className="onboarding-container">
          <div style={{ marginBottom: "45px", textAlign: "center", marginTop: "4px", fontSize: "16px", fontWeight: "bold", color: "#1EABD7" }}>
            QUESTION {currentStep + 1} / {slides.length}
          </div>

          <div style={{ width: "100%", flex: 2 }}>{slides[currentStep]}</div>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", width: "100%", padding: "0 20px" }}>
            {currentStep > 0 && (
              <Button onClick={prevStep} circular style={{ padding: "15px 20px" }}>
                <Icon name="long arrow alternate left" /> Précédent
              </Button>
            )}
            {currentStep < slides.length - 1 && (
              <Button
                primary
                circular
                style={{ padding: "15px 20px", marginLeft: "auto" }}
                onClick={nextStep}
                disabled={currentStep === 3 && (answers.needs?.length === 0 || answers.needs?.length > 3)}
              >
                Suivant <Icon name="long arrow alternate right" />
              </Button>
            )}
          </div>
        </div>
      )}{" "}
    </div>
  );
};

const Welcome = () => {
  return (
    <div key="welcome">
      <Header as="h2" textAlign="center">
        🎉 Bienvenue chez VIZYGOOD – Mission : Sauver vos yeux et votre dos ! 🎉
      </Header>
      <div className="question">
        <p>Avant de démarrer, faisons un peu plus connaissance... avec le sourire et sans stress.</p>
        <p>C'est juste pour mieux vous aider ! 😄</p>
      </div>
    </div>
  );
};

const Gender = ({ answers, setAnswers }) => {
  return (
    <div key="gender">
      <Header as="h2" textAlign="center">
        🕵️‍♂️ Vous êtes…
      </Header>
      <div className="question">
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Radio label="Un Homme" name="gender" value="male" checked={answers.gender === "male"} onChange={() => setAnswers({ ...answers, gender: "male" })} />
          <Radio label="Une Femme" name="gender" value="female" checked={answers.gender === "female"} onChange={() => setAnswers({ ...answers, gender: "female" })} />
          <Radio
            label="Un expert de la discrétion (Je ne souhaite pas répondre)"
            name="gender"
            value="undisclosed"
            checked={answers.gender === "undisclosed"}
            onChange={() => setAnswers({ ...answers, gender: "undisclosed" })}
          />
        </div>
      </div>
    </div>
  );
};

const AgeRange = ({ answers, setAnswers }) => {
  return (
    <div key="age">
      <Header as="h2" textAlign="center">
        📅 Votre tranche d'âge :
      </Header>
      <div className="question">
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Radio
            label="18-30 ans (un(e) jeune explorateur/trice)"
            name="age"
            value="18-30"
            checked={answers.age === "18-30"}
            onChange={() => setAnswers({ ...answers, age: "18-30" })}
          />
          <Radio
            label="31-45 ans (un(e) aventurier/ière confirmé(e))"
            name="age"
            value="31-45"
            checked={answers.age === "31-45"}
            onChange={() => setAnswers({ ...answers, age: "31-45" })}
          />
          <Radio label="46-65 ans (un sage expérimenté)" name="age" value="46-65" checked={answers.age === "46-65"} onChange={() => setAnswers({ ...answers, age: "46-65" })} />
          <Radio label="66 ans et plus (une légende vivante)" name="age" value="66+" checked={answers.age === "66+"} onChange={() => setAnswers({ ...answers, age: "66+" })} />
        </div>
      </div>
    </div>
  );
};

const Needs = ({ answers, setAnswers }) => {
  const handleMultipleChoice = (field, value) => {
    if (answers[field].includes(value)) {
      setAnswers({ ...answers, [field]: answers[field].filter((item) => item !== value) });
    } else {
      if (answers[field].length >= 3) {
        return;
      }
      setAnswers({ ...answers, [field]: [...answers[field], value] });
    }
  };
  return (
    <div key="needs">
      <Header as="h2" textAlign="center">
        👀 Quels sont vos besoins face aux écrans ?
      </Header>
      <div className="question">
        <p>(Choisissez 3 options maximum, on ne peut pas tout régler d'un coup 😜)</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Checkbox label="Avoir des yeux toniques et pleins de vie" checked={answers.needs?.includes("tonic-eyes")} onChange={() => handleMultipleChoice("needs", "tonic-eyes")} />
          <Checkbox
            label="Avoir une posture équilibrée comme un funambule"
            checked={answers.needs?.includes("balanced-posture")}
            onChange={() => handleMultipleChoice("needs", "balanced-posture")}
          />
          <Checkbox label="Acquérir des habitudes saines" checked={answers.needs?.includes("healthy-habits")} onChange={() => handleMultipleChoice("needs", "healthy-habits")} />
          <Checkbox
            label="Être moins fatigué(e) en fin de journée"
            checked={answers.needs?.includes("less-fatigue")}
            onChange={() => handleMultipleChoice("needs", "less-fatigue")}
          />
          <Checkbox label="M'entretenir tout simplement" checked={answers.needs?.includes("maintenance")} onChange={() => handleMultipleChoice("needs", "maintenance")} />
        </div>
        {answers.needs?.length > 3 && <p style={{ color: "red", marginTop: "10px" }}>Vous avez sélectionné plus de 3 options. Veuillez en désélectionner.</p>}
      </div>
    </div>
  );
};

const Transition = () => {
  return (
    <div key="transition">
      <Header as="h2" textAlign="center">
        Top ! Nous vous suivons !
      </Header>
      <div className="question">
        <p>➡️ Nous allons vous concocter un programme sur-mesure.</p>
        <p>Mais avant, pouvez-vous nous en dire plus sur vous ?</p>
      </div>
    </div>
  );
};

const ScreenTime = ({ answers, setAnswers }) => {
  return (
    <div key="screenTime">
      <Header as="h2" textAlign="center">
        📱 Combien de temps passez-vous chaque jour sur les écrans ?
      </Header>
      <div className="question">
        <p>(Tout écran confondu : boulot, maison, TV, téléphone… oui, oui, tout compte ! 😆)</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Radio
            label="Moins de 4h (Bravo, vous avez une vie sociale ! 🎉)"
            name="screenTime"
            value="<4"
            checked={answers.screenTime === "<4"}
            onChange={() => setAnswers({ ...answers, screenTime: "<4" })}
          />
          <Radio
            label="4 à 7h (Une utilisation raisonnable, on vous félicite 👌)"
            name="screenTime"
            value="4-7"
            checked={answers.screenTime === "4-7"}
            onChange={() => setAnswers({ ...answers, screenTime: "4-7" })}
          />
          <Radio
            label="7 à 10h (Vous et votre écran, une grande histoire d'amour 📲)"
            name="screenTime"
            value="7-10"
            checked={answers.screenTime === "7-10"}
            onChange={() => setAnswers({ ...answers, screenTime: "7-10" })}
          />
          <Radio
            label="Plus de 10h (Vous êtes officiellement un(e) cyborg 💾)"
            name="screenTime"
            value="10+"
            checked={answers.screenTime === "10+"}
            onChange={() => setAnswers({ ...answers, screenTime: "10+" })}
          />
        </div>
      </div>
    </div>
  );
};

const CurrentPractices = ({ answers, setAnswers }) => {
  const handleMultipleChoice = (value) => {
    let updatedPractices;
    if (value === "nothing") {
      updatedPractices = ["nothing"];
    } else if (answers.currentPractices?.includes("nothing")) {
      updatedPractices = [value];
    } else if (answers.currentPractices?.includes(value)) {
      updatedPractices = answers.currentPractices.filter((item) => item !== value);
    } else {
      updatedPractices = [...(answers.currentPractices || []), value];
    }
    setAnswers({ ...answers, currentPractices: updatedPractices });
  };
  return (
    <div key="practices">
      <Header as="h2" textAlign="center">
        💪 Faites-vous déjà quelque chose pour vos yeux et votre posture ?
      </Header>
      <div className="question">
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Checkbox
            label="Des pauses régulières (Bravo, vous savez vous détendre)"
            checked={answers.currentPractices?.includes("breaks")}
            onChange={() => handleMultipleChoice("breaks")}
          />
          <Checkbox
            label="Des exercices visuels (Vos yeux sont en pleine forme)"
            checked={answers.currentPractices?.includes("visual-exercises")}
            onChange={() => handleMultipleChoice("visual-exercises")}
          />
          <Checkbox
            label="Des étirements du dos (Vous êtes souple comme un roseau)"
            checked={answers.currentPractices?.includes("stretching")}
            onChange={() => handleMultipleChoice("stretching")}
          />
          <Checkbox
            label="Je ne fais rien de tout ça… mais j'y pense (Pas de panique, on est là pour ça !)"
            checked={answers.currentPractices?.includes("nothing")}
            onChange={() => handleMultipleChoice("nothing")}
          />
        </div>
      </div>
    </div>
  );
};

const VisualIssues = ({ answers, setAnswers }) => {
  const handleMultipleChoice = (value) => {
    let updatedIssues;
    if (value === "none") {
      updatedIssues = ["none"];
    } else if (answers.visualIssues?.includes("none")) {
      updatedIssues = [value];
    } else if (answers.visualIssues?.includes(value)) {
      updatedIssues = answers.visualIssues.filter((item) => item !== value);
    } else {
      updatedIssues = [...(answers.visualIssues || []), value];
    }
    setAnswers({ ...answers, visualIssues: updatedIssues });
  };

  return (
    <div key="visualIssues">
      <Header as="h2" textAlign="center">
        😵 Avez-vous des troubles visuels ?
      </Header>
      <div className="question">
        <p>(Cochez ce qui vous concerne)</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Checkbox
            label="Fatigue visuelle (La team des yeux explosés 😵)"
            checked={answers.visualIssues?.includes("VisualFatigue")} // VisualFatigue
            onChange={() => handleMultipleChoice("VisualFatigue")}
          />
          <Checkbox
            label="Larmoiements (On dira que c'est l'émotion 😢)"
            checked={answers.visualIssues?.includes("Larmoiements")} // Larmoiements
            onChange={() => handleMultipleChoice("Larmoiements")}
          />
          <Checkbox
            label="Yeux secs (Sensation désert du Sahara 🌵)"
            checked={answers.visualIssues?.includes("Sechese")} // Sechese
            onChange={() => handleMultipleChoice("Sechese")}
          />
          <Checkbox
            label="Picotements (Comme si un mini-sable mouvant était tombé dans vos yeux)"
            checked={answers.visualIssues?.includes("Picotements")} // Picotements
            onChange={() => handleMultipleChoice("Picotements")}
          />
          <Checkbox
            label="Vision floue (Ou alors il faut juste nettoyer l'écran ? 🤔)"
            checked={answers.visualIssues?.includes("VisionFloue")} // VisionFloue
            onChange={() => handleMultipleChoice("VisionFloue")}
          />
          <Checkbox
            label="Maux de tête et/ou migraines (Aïe, ça cogne 😣)"
            checked={answers.visualIssues?.includes("Migraines")} // Migraines
            onChange={() => handleMultipleChoice("Migraines")}
          />
          <Checkbox
            label="Vision double (Si vous voyez ce questionnaire en double, c'est peut-être un signe 😅)"
            checked={answers.visualIssues?.includes("VisionDouble")} // VisionDouble
            onChange={() => handleMultipleChoice("VisionDouble")}
          />
          <Checkbox
            label="Troubles de la concentration (Attendez, de quoi on parlait déjà ? 🤯)"
            checked={answers.visualIssues?.includes("Concentration")} // Concentration
            onChange={() => handleMultipleChoice("Concentration")}
          />
          <Checkbox
            label="Aucune gêne visuelle (Vous avez des yeux en béton armé, bravo ! 👏)"
            checked={answers.visualIssues?.includes("none")} // Aucune gêne visuelle
            onChange={() => handleMultipleChoice("none")}
          />
        </div>
      </div>
    </div>
  );
};

const PostureIssues = ({ answers, setAnswers }) => {
  const handleMultipleChoice = (value) => {
    let updatedIssues;
    if (value === "none") {
      updatedIssues = ["none"];
    } else if (answers.postureIssues?.includes("none")) {
      updatedIssues = [value];
    } else if (answers.postureIssues?.includes(value)) {
      updatedIssues = answers.postureIssues.filter((item) => item !== value);
    } else {
      updatedIssues = [...(answers.postureIssues || []), value];
    }
    setAnswers({ ...answers, postureIssues: updatedIssues });
  };

  return (
    <div key="postureIssues">
      <Header as="h2" textAlign="center">
        🦴 Et côté posture, ça va ?
      </Header>
      <div className="question">
        <p>(Cochez ce qui vous concerne)</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Checkbox
            label="Douleurs cervicales (Votre cou ne vous dit pas merci 😩)"
            checked={answers.postureIssues?.includes("cervical")} //
            onChange={() => handleMultipleChoice("cervical")}
          />
          <Checkbox
            label="Mal au cou (Oui, il y a une différence avec la précédente, et vous le savez ! 😆)"
            checked={answers.postureIssues?.includes("neck")}
            onChange={() => handleMultipleChoice("neck")}
          />
          <Checkbox
            label="Douleurs aux épaules (Porter un sac invisible de 10kg, ce n'est pas une bonne idée 🎒)"
            checked={answers.postureIssues?.includes("shoulders")}
            onChange={() => handleMultipleChoice("shoulders")}
          />
          <Checkbox label="Douleurs lombalgiques (Votre dos proteste 📢)" checked={answers.postureIssues?.includes("back")} onChange={() => handleMultipleChoice("back")} />
          <Checkbox
            label="Aucune gêne posturale (Vous êtes en mode moine Shaolin 🧘)"
            checked={answers.postureIssues?.includes("none")}
            onChange={() => handleMultipleChoice("none")}
          />
        </div>
      </div>
    </div>
  );
};

const ThankYou = () => {
  return (
    <div key="thanks">
      <Header as="h2" textAlign="center">
        Merci pour vos réponses !
      </Header>
      <div className="question">
        <p>Nous calculons votre indice de risque... 3, 2, 1 bingo !</p>
      </div>
    </div>
  );
};

const RiskIndex = ({ answers }) => {
  const [riskIndex, setRiskIndex] = useState(null);

  useEffect(() => {
    calculateRiskIndex();
  }, []);

  const calculateRiskIndex = () => {
    // Simple risk calculation logic
    let risk = 0;

    // Screen time factor
    if (answers.screenTime === "10+") risk += 30;
    else if (answers.screenTime === "7-10") risk += 20;
    else if (answers.screenTime === "4-7") risk += 10;

    // Current practices factor (inverse - not doing anything increases risk)
    if (answers.currentPractices === "nothing") risk += 15;

    // Visual issues factor
    risk += answers.visualIssues.length * 5;

    // Posture issues factor
    risk += answers.postureIssues.length * 5;

    // Normalize to 0-100
    risk = Math.min(Math.max(risk, 0), 100);

    setRiskIndex(risk);
  };
  const getRiskLevel = () => {
    if (riskIndex > 70) return { level: "Élevé", color: "red" };
    if (riskIndex > 30) return { level: "Moyen", color: "orange" };
    return { level: "Faible", color: "green" };
  };

  return (
    <div key="riskIndex">
      <Header as="h2" textAlign="center">
        📊 Calcul de votre "indice de risque" 🔎
      </Header>
      <div className="question">
        {riskIndex === null ? (
          <>
            <p>Nous analysons vos réponses… patience, ça charge !</p>
            <Loader active inline="centered" />
          </>
        ) : (
          <>
            <VitalityGauge Percentage={riskIndex} />
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <Header as="h3" style={{ color: getRiskLevel().color }}>
                {getRiskLevel().level} risque : {riskIndex}%
              </Header>
              {getRiskLevel().level === "Faible" && <p>🟢 Continuez comme ça, vous êtes sur la bonne voie ! 🚀</p>}
              {getRiskLevel().level === "Moyen" && <p>🟠 Rien d'alarmant, mais quelques ajustements vous feraient du bien 👌</p>}
              {getRiskLevel().level === "Élevé" && <p>🔴 Pas de panique, vous ne pouvez que vous améliorer et nous allons vous y aider 🚑💆‍♂️</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Program = ({ onClick }) => {
  return (
    <div key="program">
      <Header as="h2" textAlign="center">
        🎁 Voici votre programme personnalisé !
      </Header>
      <div className="question">
        <p>📩 Un cocktail d'astuces, d'exercices et de conseils sur-mesure pour vous remettre d'aplomb.</p>
        <p>Vos yeux et votre dos vous diront merci ! 🙌</p>
        <Button primary fluid size="large" onClick={onClick} style={{ marginTop: "20px" }}>
          Commencer mon programme
        </Button>
      </div>
    </div>
  );
};

const Contact = ({ answers }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      answers.name = formData.name;
      answers.email = formData.email;
      await api.post(`/api/questionnaire`, answers);

      await api.post("/api/questionnaire/contact", formData);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting contact form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div style={{ textAlign: "center", fontSize: "18px", marginTop: "20px" }}>
        <Icon name="check circle" color="green" size="large" />
        <p style={{ marginTop: "20px" }}>Merci pour votre message ! Nous vous répondrons dans les plus brefs délais.</p>
      </div>
    );
  }

  return (
    <div key="contact" style={{ width: "100%", maxWidth: "550px", margin: "30px auto 0" }}>
      <Header as="h2" textAlign="center">
        📞 Besoin d'aide ou de renseignements ?
      </Header>
      <div className="question">
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="name">Nom</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="4"
              style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
            />
          </div>

          <Button primary fluid size="large" type="submit" disabled={isSubmitting}>
            {isSubmitting ? <Loader active inline="centered" size="small" /> : "Envoyer"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Onboarding;
